import { Box, Stack, Tooltip } from "@mui/material";
import { useAppSelector } from "@common-reducers/hooks/store.hook";
import { memo } from "react";

type BatteryOption = {
    _id?: string;
    order?: number;
    count?: number;
    bgcolor?: string;
    label?: string;
};

interface Props {
    values: {
        selectedOptionsCloned: unknown[];
        tasksLength: number;
    };
}

function BatterySumCell({
    values,
}: Props) {
    const selectedOptionsRaw = (values.selectedOptionsCloned ?? []) as BatteryOption[];
    const selectedOptions = selectedOptionsRaw.filter((obj: BatteryOption, index: number, self: BatteryOption[]) => {
        return index === self.findIndex((t: BatteryOption) => t?._id === obj?._id);
    });
    const direction = useAppSelector((state) => state.UserReducer.direction);

    const sortedSelectedOptions = selectedOptions.sort((a: BatteryOption, b: BatteryOption) => (a?.order ?? 0) - (b?.order ?? 0));

    const getPercentage = (optionCount: number, totalTasks: number): number => {
        const percentage = (optionCount / totalTasks * 100);
        return Number(percentage.toFixed(2));
    }
    let completedTasks = sortedSelectedOptions.reduce((total: number, option: BatteryOption) => total + (option ? (option.count ?? 0) : 0), 0);

    let remainingPercentage = getPercentage(values.tasksLength - completedTasks, values.tasksLength);


    const getBorderRadius = (
        direction: string,
        index: number,
        isStart: boolean,
        isEnd: boolean
    ) => {
        if (direction === "ltr") {
            return {
                borderTopLeftRadius: isStart ? "5px" : "0px",
                borderBottomLeftRadius: isStart ? "5px" : "0px",
                borderTopRightRadius: isEnd ? "5px" : "0px",
                borderBottomRightRadius: isEnd ? "5px" : "0px"
            };
        } else {
            return {
                borderTopRightRadius: isStart ? "5px" : "0px",
                borderBottomRightRadius: isStart ? "5px" : "0px",
                borderTopLeftRadius: isEnd ? "5px" : "0px",
                borderBottomLeftRadius: isEnd ? "5px" : "0px"
            };
        }
    };

    return (
        <Stack direction="row" alignItems={"center"} flexGrow={1} textAlign={"center"}
            sx={{ background: 'var(--color-surface)', height: "80%", padding: "0 10px" }} >
            {sortedSelectedOptions.map((column: BatteryOption, index: number) => (
                <Tooltip key={index} disableInteractive componentsProps={{
                    tooltip: {
                        sx: {
                            bgcolor: "var(--color-surface)",
                            padding: "4px 18px",
                            justifyContent: "center",
                            border: '1px solid transparent',
                            alignItems: "center",
                            color: "rgb(0, 55, 80)",
                            boxShadow: "0 5px 12px rgb(0 0 0 / 15%)",
                            "& .MuiTooltip-arrow": {
                                color: "var(--color-surface)",
                            },
                        },
                    },
                }} title={
                    <Stack justifyContent={"center"} p={2} minWidth={"140px"} spacing={2} >
                        <Stack justifyContent={"center"} alignItems={"center"} sx={{
                            background: column?.bgcolor,
                            color: "var(--color-surface)",
                            textTransform: "capitalize",
                            boxShadow: "none",
                            fontSize: 12,
                            borderRadius: "5px",
                            fontWeight: "600",
                            fontFamily: "'Inter', 'Assistant', sans-serif",
                            height: 28,
                        }}>
                            {column?.label}
                        </Stack>

                        <Stack direction="row" justifyContent={"center"} sx={{ fontSize: 14 }}>{column?.count}/{values.tasksLength} -
                            {getPercentage(column?.count ?? 0, values.tasksLength) + "%"}</Stack>
                    </Stack>
                } arrow placement="top">
                    <Box key={"battery_" + index}
                        sx={{
                            width: (column?.count ?? 0) / values.tasksLength * 100 + "%",
                            background: column?.bgcolor,
                            height: "20px",
                            ...getBorderRadius(
                                direction,
                                index,
                                index === 0,
                                index === sortedSelectedOptions.length - 1 && remainingPercentage === 0
                            )
                        }}>
                    </Box>
                </Tooltip>
            ))}
            {remainingPercentage > 0 && <Box sx={{
                width: remainingPercentage + "%",
                background: "#c4c4c4f5",
                height: "20px",
                ...getBorderRadius(
                    direction,
                    -1,  // Using -1 since this isn't part of sortedSelectedOptions array
                    remainingPercentage === 100,
                    remainingPercentage > 0
                )
            }}></Box>}
        </Stack>
    );
}

export default memo(BatterySumCell);
